import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PositionService } from '../../../../services/position.service';
import { ErrorService } from '../../../../shared/services/error.service';
import { Position } from '../../../../models/Position';

@Component({
  selector: 'app-position-form',
  templateUrl: './position-form.component.html',
  styleUrls: ['./position-form.component.css']
})
export class PositionFormComponent implements OnInit {
  positionForm: FormGroup;
  departmentId: number = 0;
  isSubmitting = false;
  isEditMode = false;
  positionId: number | null = null;

  levelOptions = [
    { value: 'Júnior', label: 'Júnior' },
    { value: 'Pleno', label: 'Pleno' },
    { value: 'Sênior', label: 'Sênior' },
    { value: 'Especialista', label: 'Especialista' },
    { value: 'Coordenador', label: 'Coordenador' },
    { value: 'Gerente', label: 'Gerente' },
    { value: 'Diretor', label: 'Diretor' }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private positionService: PositionService,
    private errorService: ErrorService
  ) {
    this.positionForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
      level: ['', Validators.maxLength(50)],
      description: ['', Validators.maxLength(500)],
      defaultWorkloadHours: [8, [Validators.required, Validators.min(1), Validators.max(24)]],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.departmentId = +params['id'];
      const positionIdParam = params['positionId'];

      if (!this.departmentId) {
        this.errorService.showErrorMessage('Departamento não encontrado');
        this.router.navigate(['/hr/departments']);
        return;
      }

      if (positionIdParam) {
        this.isEditMode = true;
        this.positionId = +positionIdParam;

        // Visualizar cargo antes de editar
        this.positionService.getPositionById(this.positionId).subscribe({
          next: (position: Position) => {
            this.positionForm.patchValue({
              name: position.name,
              level: position.level || '',
              description: position.description || '',
              defaultWorkloadHours: position.defaultWorkloadHours,
              isActive: position.isActive
            });
          },
          error: (err) => {
            this.errorService.handleHttpError(err);
          }
        });
      }
    });
  }

  onSubmit(): void {
    if (this.positionForm.invalid) {
      this.positionForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    const formValue = this.positionForm.value;
    const positionData = {
      departmentId: this.departmentId,
      name: formValue.name,
      level: formValue.level || null,
      description: formValue.description || null,
      defaultWorkloadHours: formValue.defaultWorkloadHours,
      isActive: formValue.isActive !== undefined ? formValue.isActive : true
    };

    if (this.isEditMode && this.positionId) {
      // Atualizar cargo existente
      this.positionService.updatePosition(this.positionId, positionData).subscribe({
        next: (response: Position) => {
          this.isSubmitting = false;
          this.router.navigate(['/hr/departments', this.departmentId, 'positions']);
        },
        error: (err: any) => {
          this.errorService.handleHttpError(err);
          this.isSubmitting = false;
        }
      });
    } else {
      // Criar novo cargo
      this.positionService.createPosition(positionData).subscribe({
        next: (response: Position) => {
          this.isSubmitting = false;
          this.router.navigate(['/hr/departments', this.departmentId, 'positions']);
        },
        error: (err: any) => {
          this.errorService.handleHttpError(err);
          this.isSubmitting = false;
        }
      });
    }
  }

  onCancel(): void {
    this.router.navigate(['/hr/departments', this.departmentId, 'positions']);
  }

  getFieldError(fieldName: string): string {
    const field = this.positionForm.get(fieldName);
    if (field && field.invalid && (field.touched || field.dirty)) {
      if (field.errors?.['required']) {
        return `${this.getFieldLabel(fieldName)} é obrigatório`;
      }
      if (field.errors?.['minlength']) {
        return `${this.getFieldLabel(fieldName)} deve ter no mínimo ${field.errors['minlength'].requiredLength} caracteres`;
      }
      if (field.errors?.['maxlength']) {
        return `${this.getFieldLabel(fieldName)} deve ter no máximo ${field.errors['maxlength'].requiredLength} caracteres`;
      }
      if (field.errors?.['min']) {
        return `${this.getFieldLabel(fieldName)} deve ser no mínimo ${field.errors['min'].min}`;
      }
      if (field.errors?.['max']) {
        return `${this.getFieldLabel(fieldName)} deve ser no máximo ${field.errors['max'].max}`;
      }
    }
    return '';
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.positionForm.get(fieldName);
    return !!(field && field.invalid && (field.touched || field.dirty));
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Nome',
      level: 'Nível',
      description: 'Descrição',
      defaultWorkloadHours: 'Carga Horária',
      isActive: 'Status'
    };
    return labels[fieldName] || fieldName;
  }
}

